import { AbModal } from '@/scripts/modules/ab-modal/ab-modal.class';
import { createDomElementHandler as el, createSvg } from '@/scripts/utils/dom.utils';

/** Optional mobile presentation of the existing navigation, not a second TOC model. */
export class StickyTocMobilePanel {
  #desktop = window.matchMedia('(min-width: 1024px)');
  #button = el('button', 'ab-sticky-toc__mobile-trigger', {
    type: 'button', 'aria-expanded': 'false', 'aria-haspopup': 'dialog',
  }) as HTMLButtonElement;
  #label = el('span');
  #title = '';
  #modal?: AbModal;
  #content?: HTMLElement;
  #dialog?: HTMLElement;
  #active = false;
  #restoreFocus = true;

  constructor(private host: HTMLElement, private onClose: () => void) {
    const icon = createSvg('icon-list-bulleted');
    icon.setAttribute('aria-hidden', 'true');
    this.#button.append(icon, this.#label);
    this.#button.hidden = true;
    host.append(this.#button);
    host.classList.add('ab-sticky-toc--mobile-panel');
    this.#button.addEventListener('click', this.#open);
    this.#desktop.addEventListener('change', this.#syncVisibility);
  }

  refresh() {
    this.#title = this.host.querySelector('.abtoc__title')?.textContent
      ?? this.host.querySelector('.abtoc__nav')?.getAttribute('aria-label') ?? '';
    this.#label.textContent = this.host.getAttribute('data-mobile-label') ?? this.#title;
    if (this.#content) {
      const focused = this.#content.contains(document.activeElement)
        ? (document.activeElement as HTMLAnchorElement).hash : undefined;
      const scrollArea = this.#dialog?.querySelector('.ab-modal__inner');
      const scrollTop = scrollArea?.scrollTop ?? 0;
      this.#copyNavigation();
      if (focused) Array.from(this.#content.querySelectorAll<HTMLAnchorElement>('a'))
        .find((link) => link.hash === focused)?.focus({ preventScroll: true });
      if (scrollArea) scrollArea.scrollTop = scrollTop;
      const title = this.#dialog?.querySelector('.ab-modal__header-title');
      if (title) title.textContent = this.#title;
    }
    this.#syncVisibility();
  }

  setActive(active: boolean) {
    // Loading images/content can move the background while its scroll is locked.
    // Keep the chosen panel open until the user closes it or leaves mobile layout.
    if (this.#modal) return;
    this.#active = active;
    this.#syncVisibility();
  }

  setActiveLink(targetId: string) {
    this.#content?.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((link) => {
      const active = link.hash === `#${targetId}`;
      link.classList.toggle('abtoc__link--active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  destroy() {
    this.#close(false);
    this.#desktop.removeEventListener('change', this.#syncVisibility);
    this.#button.removeEventListener('click', this.#open);
    this.#button.remove();
    this.host.classList.remove('ab-sticky-toc--mobile-panel');
  }

  #syncVisibility = () => {
    this.#button.hidden = this.#desktop.matches || !this.#active;
    if (this.#button.hidden) this.#close(false);
  };

  #copyNavigation() {
    const nav = this.host.querySelector('.abtoc__nav');
    // Only the navigation is copied: the section/title IDs stay in the canonical TOC.
    this.#content?.replaceChildren(...(nav ? [nav.cloneNode(true)] : []));
  }

  #open = () => {
    if (this.#modal || this.#button.hidden) return;
    this.#content = el('div', 'ab-sticky-toc__panel-content');
    this.#copyNavigation();
    this.#content.addEventListener('click', this.#onNavigate);
    this.#restoreFocus = true;
    this.#modal = new AbModal({
      id: 'guide-toc', autoOpen: false, handleBackBrowser: false, hideBodyOverflow: true,
      attributes: {
        classesModal: 'ab-modal--toc', classesBackdrop: 'ab-modal__backdrop--dark-50',
        classesInner: '', classesWrapper: '',
        header: { show: true, closeButton: true, closeButtonText: 'Chiudi' },
      },
      handlers: { onClose: () => {
        this.#dialog?.removeEventListener('keydown', this.#onKeydown);
        this.#content?.removeEventListener('click', this.#onNavigate);
        this.#modal = undefined; this.#content = undefined; this.#dialog = undefined;
        this.#button.setAttribute('aria-expanded', 'false');
        this.#button.removeAttribute('aria-controls');
        if (this.#restoreFocus && this.host.isConnected && !this.#button.hidden) {
          this.#button.focus({ preventScroll: true });
        }
        if (this.host.isConnected) this.onClose();
      } },
    }, this.#content);
    this.#modal.show();
    this.#dialog = document.getElementById(this.#modal.id)!;
    const title = el('h3', 'ab-modal__header-title', { id: `${this.#modal.id}-title` });
    // Custom editorial titles must remain text, including in the modal header.
    title.textContent = this.#title;
    this.#dialog.querySelector('.ab-modal__header')?.prepend(title);
    this.#dialog.setAttribute('role', 'dialog');
    this.#dialog.setAttribute('aria-modal', 'true');
    this.#dialog.setAttribute('aria-labelledby', title.id);
    this.#dialog.addEventListener('keydown', this.#onKeydown);
    this.#button.setAttribute('aria-expanded', 'true');
    this.#button.setAttribute('aria-controls', this.#modal.id);
    this.#dialog.querySelector<HTMLButtonElement>('.ab-modal__close')?.focus();
  };

  #close(restoreFocus: boolean) {
    this.#restoreFocus = restoreFocus;
    this.#modal?.hide();
  }

  #onNavigate = (event: MouseEvent) => {
    const link = (event.target as Element).closest<HTMLAnchorElement>('a[href^="#"]');
    if (!link || !this.#content?.contains(link)) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    this.#close(false);
    if (target) {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
    // Keep the native anchor (and theme smooth-scroll) navigation and URL semantics.
  };

  #onKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const controls = Array.from(this.#dialog?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]') ?? []);
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first?.focus();
    }
  };
}
